import React, { useLayoutEffect, useState } from 'react';
import { Calendar, Clock, ChevronRight, Home, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Annotation } from '../../types/annotation';
import { useAppContext } from '../../Context';

interface ProjectCardProps {
  title: string;
  duration: string;
  lastUpdated: string;
  progress: number;
  status: 'active' | 'completed';
  onClick: () => void;
}

export type Status = 'active' | 'completed';

export interface Participant {
  id: string;
  title: string;
  duration: string;
  lastUpdated: string;
  progress: number;
  status: Status;
  annotations: Annotation[];
}

const ProjectCard: React.FC<ProjectCardProps> = ({ title, duration, lastUpdated, progress, status, onClick }) => {
    return (
      <div className="tw-border tw-rounded-lg tw-p-4 tw-shadow-md tw-bg-white tw-text-black" onClick={onClick}>
        <div className="tw-flex tw-justify-between tw-items-center">
          <h2 className="tw-text-lg tw-font-semibold">{title}</h2>
          <span
            className={`tw-px-2 tw-py-1 tw-rounded-full tw-text-sm tw-font-medium ${
              status === 'active' ? 'tw-bg-black tw-text-white' : 'tw-bg-gray-200 tw-text-black'
            }`}
          >
            {status}
          </span>
        </div>
        <div className="tw-flex tw-items-center tw-space-x-2 tw-text-black tw-mt-2">
          <Calendar size={16} />
          <span>{duration}</span>
          <Clock size={16} />
          <span>{lastUpdated}</span>
        </div>
        <div className="tw-mt-4">
          <div className="tw-flex tw-justify-between tw-text-sm tw-font-medium tw-text-black">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <div className="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2.5 tw-mt-2">
            <div
              className="tw-bg-black tw-h-2.5 tw-rounded-full"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
        <div className="tw-mt-4 tw-text-black tw-flex tw-items-center tw-justify-end tw-cursor-pointer hover:tw-underline" onClick={onClick}>
          View Project
          <ChevronRight size={16} className="tw-ml-1" />
        </div>
      </div>
    );
  };

const Participants: React.FC = () => {
  const projects: { title: string; duration: string; lastUpdated: string; progress: number; status: 'active' | 'completed' }[] = [
    {
      title: 'Matthew S. Study',
      duration: '3 days',
      lastUpdated: '5d ago',
      progress: 33,
      status: 'active',
    },
    {
      title: 'Sarah J. Longitudinal',
      duration: '3 days',
      lastUpdated: '5d ago',
      progress: 67,
      status: 'active',
    },
    {
      title: 'Robert K. Sleep Study',
      duration: '2 days',
      lastUpdated: '6d ago',
      progress: 100,
      status: 'completed',
    },
  ];

const participants: Participant[] = [
  {
    id: 'p1',
    title: 'Matthew S.',
    duration: '3 days',
    lastUpdated: '5d ago',
    progress: 22,
    status: 'active',
    annotations: [],
  },
  // Insert new participant at index 1
  {
    id: 'p_new1',
    title: 'Jamie P.',
    duration: '4 days',
    lastUpdated: '4d ago',
    progress: 30,
    status: 'active',
    annotations: [],
  },
  // Insert new participant at index 2
  {
    id: 'p_new2',
    title: 'Jordan W.',
    duration: '2 days',
    lastUpdated: '7d ago',
    progress: 40,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p2',
    title: 'Daylan G.',
    duration: '3 days',
    lastUpdated: '5d ago',
    progress: 47,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p3',
    title: 'John R.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 35,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p4',
    title: 'Yolanda J.',
    duration: '3 days',
    lastUpdated: '5d ago',
    progress: 18,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p5',
    title: 'Chelsea J.',
    duration: '3 days',
    lastUpdated: '5d ago',
    progress: 29,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p6',
    title: 'Robert K.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 50,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p7',
    title: 'Kaelah B.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 14,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p8',
    title: 'Alexander B.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 40,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p9',
    title: 'Brent S.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 33,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p10',
    title: 'John P. K.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 10,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p11',
    title: 'Brad M.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 55,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p12',
    title: 'Jopseph D.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 27,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p13',
    title: 'Emma A.',
    duration: '3 days',
    lastUpdated: '5d ago',
    progress: 38,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p14',
    title: 'Michael F.',
    duration: '3 days',
    lastUpdated: '5d ago',
    progress: 44,
    status: 'active',
    annotations: [],
  },
  {
    id: 'p15',
    title: 'Mickael R.',
    duration: '2 days',
    lastUpdated: '6d ago',
    progress: 31,
    status: 'active',
    annotations: [],
  },
];

  const [filteredParticipants, setFilteredParticipants] = useState(participants);

  const navigate = useNavigate();

  const handleClick = (participantID: string) => {
    navigate(`/thermal/participants/${participantID}`);
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('Searching for participants...');

    const searchTerm = (event.target as HTMLInputElement).value;

    setFilteredParticipants(participants.filter(participant => participant.title.toLowerCase().includes(searchTerm.toLowerCase())));
  };

  const appContext = useAppContext();

  useLayoutEffect(() => {
    appContext.setFullScreen(false);
  }, []);

  return (
    <div className="tw-min-h-screen tw-bg-gray-100 tw-p-6">
        <header className="tw-mb-6">
        <nav className="tw-flex tw-items-center tw-space-x-2 tw-text-gray-700">
          <Home size={20} />
          <span className="tw-text-lg tw-font-semibold">Participants</span>
        </nav>
      </header>
      <header className="tw-mb-6">
        <h1 className="tw-text-xl tw-font-bold">All Participants</h1>
      </header>

      <div className="tw-flex tw-items-center tw-mb-6">
        <Search size={20} className="tw-mr-2 tw-text-gray-500" /> {/* Magnifying glass icon */}
        <input
          type="text"
          placeholder="Search participants..."
          className="tw-border tw-rounded-lg tw-p-2 tw-w-full tw-bg-white tw-text-black"
          onChange={handleSearch}
        />
      </div>


      <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
        {filteredParticipants?.map((participant, index) => (
          <ProjectCard key={index} {...participant} onClick={() => handleClick(participant.id)}/>
        ))}
      </div>
    </div>
  );
};

export default Participants;